/**
 * Number formatting and bar geometry for the Leader Home.
 *
 * ⭐ There are TWO bar families here and they are normalised differently. Do not
 * unify them — the difference is the design's, and it is measurable in the frame:
 *
 *  - The three vitality PILLS in the KPI band are one row split three ways, so they
 *    fill a fixed track: {@link stackWidths}. Their labels sit inside them, which
 *    forces a per-segment floor and costs strict proportionality.
 *  - The industry table's stacked BARS share ONE px-per-asset across every row, so a
 *    row's total length itself says how big that row is: {@link linearScale} over the widest
 *    row's DRAWN UNITS (see `vitalityUnits` in VitalityBar). Measured off the frame, the
 *    seven rows come out at
 *    4.26–4.71 px per asset while their total widths run 98–181px, which is what
 *    rules out a per-row (100%-stacked) reading.
 */

/**
 * ARR the way the design writes it: `$10.25M`, `$813K`, `$0`.
 *
 * Two decimals at M, none at K — that is what the mock shows, and the rounding is
 * deliberate: these are headline figures, not reconciliation numbers.
 *
 * Not reusing `fmtArr` (MotionWaveSelectPanel) or `formatCurrency` (InformationCard),
 * both of which do M/K/plain: they round to one decimal at M, and this design shows
 * two. Worth noting they exist — a third copy is the cost of that difference, and if a
 * shared formatter ever lands it should take the precision as a parameter.
 *
 * Rounding is applied BEFORE the unit is chosen at both seams, so a value cannot round
 * up into a unit it does not belong to (999_999 is `$1.00M`, never `$1000K`; 999.6 is
 * `$1K`, never `$1000`). The sign is applied after rounding, so a value in (-1, 0)
 * prints `$0` rather than `-$0`.
 */
export function formatArr(value: number | undefined | null): string {
  const v = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  const abs = Math.abs(v);
  const sign = (s: string) => (v < 0 && s !== '$0' ? `-${s}` : s);

  if (abs >= 1e6) return sign(`$${(abs / 1e6).toFixed(2)}M`);
  if (abs >= 1e3) {
    const k = Math.round(abs / 1e3);
    return k >= 1000 ? sign(`$${(abs / 1e6).toFixed(2)}M`) : sign(`$${k}K`);
  }
  const d = Math.round(abs);
  return d >= 1000 ? sign('$1K') : sign(`$${d}`);
}

/**
 * What goes where a number would when the bucket is ABSENT rather than zero.
 *
 * Only `FlowMetrics#lost` can be absent: it is the one optional bucket, and the contract
 * says why absence is not zero — "the server predates this field", not "nothing was lost".
 * A deployed service older than this bundle omits it, and printing `0 opps / $0` would
 * assert a fact nobody measured.
 *
 * An em dash rather than blank or `N/A`: it holds the cell's shape at both type sizes (42px
 * in the band, 14px in the table) and cannot be read as a figure. Callers pair it with
 * `is-absent`, which drops the cell's tone — a RED dash would read as a bad number.
 */
export const ABSENT_MARK = '—';

/**
 * Widths for a set of segments that together fill one fixed track.
 *
 * Floor first, then split the remainder in proportion: every non-zero segment
 * gets `minPx` so its own label fits inside it, and what is left of the track is
 * divided by value. Three properties fall out, and they are the reason for this
 * shape rather than a plain `value / total`:
 *   - the row exactly fills `trackPx` — whenever at least one segment is non-zero
 *     AND the floors fit; the two guarded branches below are the exceptions, and
 *     each says what it returns instead,
 *   - it is monotonic — a larger value is never a shorter segment,
 *   - every non-zero segment stays wide enough to read.
 *
 * The cost, stated plainly: **ratios are not preserved.** 40 / 20 / 16 does not
 * render as 2.5 : 1.25 : 1. Differences are preserved exactly — the gap between
 * two segments is proportional to the gap between their values. The design makes
 * the same trade: measured off the Figma frame the three pills come out at 4.40,
 * 4.95 and 5.13 px per asset, i.e. the small bands were widened to fit their own
 * numbers rather than drawn to scale.
 *
 * A zero value gets width 0, and callers must not draw a SEGMENT for it — one wide
 * enough to hold "0" would encode a magnitude it does not have. What they draw instead
 * is theirs to decide, but it has to take its width out of `trackPx` before calling
 * (VitalityPills passes the track minus its placeholders).
 */
export function stackWidths(values: number[], trackPx: number, minPx: number): number[] {
  const clean = values.map((v) => (Number.isFinite(v) && v > 0 ? v : 0));
  const nonZero = clean.filter((v) => v > 0).length;
  if (nonZero === 0) return clean.map(() => 0);

  // Track too narrow to seat the floors. Keep the labels readable and let the row
  // overflow: the number is the information, its width is only the comparison.
  if (nonZero * minPx >= trackPx) return clean.map((v) => (v > 0 ? minPx : 0));

  const spare = trackPx - nonZero * minPx;
  const total = clean.reduce((s, v) => s + v, 0);
  return clean.map((v) => (v > 0 ? minPx + (spare * v) / total : 0));
}

/**
 * Floor for a positive-but-tiny value, so a count of 1 does not vanish.
 *
 * This is where strict proportionality is deliberately broken for the table bars:
 * below roughly this many pixels the segment over-states its value. The alternative
 * — letting 1 render as a sub-pixel sliver — reads as zero, which is worse. Much
 * smaller than the pills' floor because these segments carry no text inside them.
 */
export const MIN_BAR_PX = 3;

/**
 * A linear scale shared by every bar drawn from the same basis.
 *
 * @param maxBasis the largest value the scale must fit — for the stacked vitality
 *   bars, the largest ROW TOTAL, so the biggest row fills the track and every other
 *   row is shorter in proportion.
 * @param maxPx how many pixels {@code maxBasis} maps to.
 *
 * Zero maps to zero, not to {@link MIN_BAR_PX}: a real zero must render as nothing,
 * or an industry with no assets in a band would appear to have some.
 */
export function linearScale(
  maxBasis: number,
  maxPx: number,
  minPx: number = MIN_BAR_PX,
): (value: number) => number {
  const k = maxBasis > 0 ? maxPx / maxBasis : 0;
  return (value: number) => {
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.max(minPx, value * k);
  };
}

/**
 * Largest single value — the basis for the three-month trend columns.
 *
 * The largest single MONTH, not the largest row sum: a trend bar's height is one month on its
 * own, so what has to fit the track is the biggest month anywhere in the table. (Contrast
 * `vitalityUnits`, whose bar is three bands laid end to end and therefore scales on a sum.)
 *
 * Deleted once as dead code when the vitality bars stopped needing it; back because the trend
 * columns do. If it goes callerless again, delete it again.
 */
export function maxValue(values: number[]): number {
  let max = 0;
  for (const v of values) if (Number.isFinite(v) && v > max) max = v;
  return max;
}
